import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleHelpIcon,
  PanelLeftIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react"
import { useState } from "react"
import { Link, NavLink, useLocation, useMatch } from "react-router"
import type { Project } from "@beacon/core/projects"
import { IdentityTile, Kbd } from "@/design-system/index.ts"
import { Button } from "@/primitives/button.tsx"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/primitives/sidebar.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { projectsByName } from "@/projects/order.ts"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import type { Me } from "@/sign-in/client.ts"
import { AccountMenu } from "./account-menu.tsx"
import {
  adminDestinations,
  designSystem,
  destinations,
  inAdministration,
  type Destination,
} from "./destinations.ts"
import { QueryDevtools } from "./query-devtools.tsx"
import { readFolds, writeFold, type SidebarSection } from "./sections.ts"

/*
 * The sidebar, on the canvas beside the page: the Member's own row that opens the account menu, the
 * command palette's search, the pages every Member has, their Projects with My Project first, and
 * at the foot the shortcuts and the collapse toggle. On an Administration page an Admin sees it
 * turned to Administration instead: the way back to where they were, and the Admin's pages. The
 * current page sits on a grey fill; nav is never teal. Collapsed to icons, each row names itself
 * and its G-chord in a tooltip.
 */

/** How many of a Member's Projects the sidebar lists before "All Projects". */
const projectsShown = 8

/**
 * A page in the sidebar. A `nested` one stays current on the pages beneath it too, unless
 * `elsewhere` says another row is the current one there.
 */
function DestinationItem({
  destination: { to, title, Icon, chord, nested },
  badge,
  elsewhere = false,
}: {
  destination: Omit<Destination, "chord"> & { chord?: ShortcutId }
  badge?: number
  elsewhere?: boolean
}) {
  const current = useMatch({ path: to, end: !nested }) && !elsewhere
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to={to} end={!nested} />}
        isActive={Boolean(current)}
        tooltip={{
          children: (
            <>
              {title}
              {chord && <Kbd shortcut={chord} />}
            </>
          ),
        }}
      >
        <Icon />
        <span>{title}</span>
      </SidebarMenuButton>
      {badge !== undefined && badge > 0 && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
    </SidebarMenuItem>
  )
}

function ProjectItem({ project: { key, name } }: { project: Project }) {
  const current = useMatch(`/projects/${key}/*`)
  return (
    <SidebarMenuItem>
      <SidebarMenuButton render={<NavLink to={`/projects/${key}`} />} isActive={Boolean(current)}>
        <IdentityTile projectKey={key} size={16} />
        <span>{name}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * Whether a section is open, and the toggle that folds or opens it and remembers so in this browser.
 */
function useFold(section: SidebarSection) {
  const [open, setOpen] = useState(() => !readFolds()[section])
  function toggle() {
    setOpen(!open)
    writeFold(section, open)
  }
  return [open, toggle] as const
}

/**
 * A section's label as the button that folds it, with a chevron shown on hover, and to a keyboard
 * as it reaches the label, so the fold is seen both ways. Hidden on the icon rail, which has no
 * labels.
 */
function FoldLabel({
  open,
  onToggle,
  children,
}: {
  open: boolean
  onToggle: () => void
  children: string
}) {
  return (
    <SidebarGroupLabel
      render={<button type="button" onClick={onToggle} aria-expanded={open} />}
      className="gap-1 hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden [&>svg]:size-3.5"
    >
      {children}
      <ChevronRightIcon
        aria-hidden
        className={
          open
            ? "rotate-90 opacity-0 transition duration-100 group-hover/sidebar-group:opacity-100 in-focus-visible:opacity-100"
            : "opacity-0 transition duration-100 group-hover/sidebar-group:opacity-100 in-focus-visible:opacity-100"
        }
      />
    </SidebarGroupLabel>
  )
}

/**
 * My Project first, a row like the rest, then the Member's other Projects, up to eight by
 * name, then a link to the rest when there are more (the Projects page is a row above, so it isn't
 * repeated when every Project is listed). The list folds away under its label and offers a new
 * Project on hover. My Project stays when the list is folded and when the
 * sidebar is collapsed to icons, where the rest of the section is hidden.
 */
function ProjectsSection({
  projects,
  myProject,
}: {
  projects: readonly Project[]
  myProject: Destination | undefined
}) {
  const [open, toggle] = useFold("projects")
  const team = projects.filter((project) => !project.personal)
  const listed = projectsByName(team).slice(0, projectsShown)

  return (
    <SidebarGroup>
      <FoldLabel open={open} onToggle={toggle}>
        Projects
      </FoldLabel>
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarGroupAction
              render={<Link to="/projects?new=1" aria-label="New Project" />}
              className="opacity-0 group-hover/sidebar-group:opacity-100 focus-visible:opacity-100 group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <PlusIcon />
        </TooltipTrigger>
        <TooltipContent side="right">New Project</TooltipContent>
      </Tooltip>
      <SidebarGroupContent>
        <SidebarMenu>{myProject && <DestinationItem destination={myProject} />}</SidebarMenu>
      </SidebarGroupContent>
      {open && (
        <SidebarGroupContent className="group-data-[collapsible=icon]:hidden">
          <SidebarMenu>
            {listed.map((project) => (
              <ProjectItem key={project.key} project={project} />
            ))}
            {team.length > listed.length && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  render={<Link to="/projects" />}
                  className="text-subtle-foreground"
                >
                  <span>All Projects</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/**
 * Administration, as the sidebar shows it on the Admin's pages: the row back to the page the Admin
 * came from, and the Admin's pages under their label, the design system's last.
 */
function AdministrationNav({ backTo }: { backTo: string }) {
  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={<Link to={backTo} />}
              tooltip="Back to Beacon"
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon />
              <span>Back to Beacon</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent role="navigation" aria-label="Administration">
        <SidebarGroup>
          <SidebarGroupLabel>Administration</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[...adminDestinations, designSystem].map((destination) => (
                <DestinationItem key={destination.to} destination={destination} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  )
}

/**
 * Where "Back to Beacon" goes: the last page the Member was on outside Administration, or Home when
 * they opened Beacon on an Administration page.
 */
function useBackTo(administration: boolean) {
  const { pathname, search } = useLocation()
  const here = `${pathname}${search}`
  const [backTo, setBackTo] = useState("/")
  if (!administration && backTo !== here) setBackTo(here)
  return backTo
}

/**
 * The foot: the shortcuts behind `?`, the cache inspector in development, and the toggle that
 * collapses the sidebar. The first two hide on the icon rail, which has room for the toggle alone.
 * ⌘B works the toggle too, bound here through the registry like every other shortcut, so it leaves
 * ⌘B to a field (bold, in the markdown editor) and to a dialog open over the page.
 */
function Foot({ onShowShortcuts }: { onShowShortcuts: () => void }) {
  const { state, toggleSidebar } = useSidebar()
  useShortcut("toggle-sidebar", toggleSidebar)
  const collapsed = state === "collapsed"
  return (
    <SidebarFooter className="mt-auto flex-row items-center">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Keyboard shortcuts"
              onClick={onShowShortcuts}
              className="group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <CircleHelpIcon />
        </TooltipTrigger>
        <TooltipContent side="top">
          Keyboard shortcuts
          <Kbd shortcut="shortcuts" />
        </TooltipContent>
      </Tooltip>
      <span className="flex group-data-[collapsible=icon]:hidden">
        <QueryDevtools />
      </span>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={toggleSidebar}
              className="ml-auto group-data-[collapsible=icon]:mr-auto"
            />
          }
        >
          <PanelLeftIcon />
        </TooltipTrigger>
        <TooltipContent side={collapsed ? "right" : "top"}>
          {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          <Kbd shortcut="toggle-sidebar" />
        </TooltipContent>
      </Tooltip>
    </SidebarFooter>
  )
}

export function AppSidebar({
  me,
  projects,
  openTickets,
  onSearch,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  /** The Member's Projects that aren't archived. */
  projects: readonly Project[]
  /** How many open tickets are assigned to the Member, once known. */
  openTickets: number | undefined
  onSearch: () => void
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const [home, myTickets, ...others] = destinations
  // My Project leads the Projects section rather than sitting among these, and the account menu
  // lists its own.
  const shown = others.filter(
    (destination) => destination.to !== "/my-project" && !destination.accountMenu,
  )
  // Once their Personal Project is made, My Project goes straight to its Board, and stays current
  // on its pages; until then it goes to the page that makes it.
  const personal = projects.find((project) => project.personal)
  const myProjectPage = destinations.find((destination) => destination.to === "/my-project")
  const myProject =
    myProjectPage && personal
      ? { ...myProjectPage, to: `/projects/${personal.key}`, nested: true as const }
      : myProjectPage
  // On its pages My Project is the current row, not Projects, which holds every other Board.
  const onPersonal = Boolean(useMatch(`/projects/${personal?.key ?? "-"}/*`)) && Boolean(personal)
  // Administration's pages turn the sidebar to it for an Admin. A Member who isn't one keeps the
  // app's sidebar on the one such page that answers them too (Jira).
  const { pathname } = useLocation()
  const administration = me.role === "admin" && inAdministration(pathname)
  const backTo = useBackTo(administration)

  if (administration) {
    return (
      <Sidebar variant="inset" collapsible="icon">
        <AdministrationNav backTo={backTo} />
        <Foot onShowShortcuts={onShowShortcuts} />
        <SidebarRail />
      </Sidebar>
    )
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className="flex h-9 items-center gap-1">
          <AccountMenu me={me} onShowShortcuts={onShowShortcuts} onSignOut={onSignOut} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Search"
                  onClick={onSearch}
                  className="group-data-[collapsible=icon]:hidden"
                />
              }
            >
              <SearchIcon />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Search
              <Kbd shortcut="command-palette" />
            </TooltipContent>
          </Tooltip>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={onSearch}
              tooltip={{
                children: (
                  <>
                    Search or jump to…
                    <Kbd shortcut="command-palette" />
                  </>
                ),
              }}
              // A quiet field on the canvas, which opens the palette rather than taking text. Its
              // gaps and its end are a step tighter than a nav row's, so the whole label fits
              // beside Ctrl K, which is wider than ⌘K; the keys never give way, the label would
              // truncate first.
              className="gap-1.5 pr-1.5 bg-background/60 font-normal text-muted-foreground ring-1 ring-sidebar-border hover:bg-background hover:text-foreground"
            >
              <SearchIcon />
              <span className="truncate">Search or jump to…</span>
              <span className="ml-auto shrink-0 group-data-[collapsible=icon]:hidden">
                <Kbd shortcut="command-palette" />
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {/* The pages, as the one navigation landmark, so a screen reader can reach or skip them. */}
      <SidebarContent role="navigation" aria-label="Main">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {home && <DestinationItem destination={home} />}
              {myTickets && <DestinationItem destination={myTickets} badge={openTickets} />}
              {shown.map((destination) => (
                <DestinationItem
                  key={destination.to}
                  destination={destination}
                  elsewhere={destination.to === "/projects" && onPersonal}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <ProjectsSection projects={projects} myProject={myProject} />
      </SidebarContent>
      <Foot onShowShortcuts={onShowShortcuts} />
      <SidebarRail />
    </Sidebar>
  )
}
